import * as React from 'react';
import PageModel from '@app/Model/PageModel';
import { PageSection, Title, Form, FormGroup, TextArea, Button, Alert } from '@patternfly/react-core';
import UserModel from '@app/Model/UserModel';
import axios from "@app/Security/AxiosAuthentication";
import ApiEndpoints from '@app/utils/ApiEndpoints';
import htmlParser from '@app/utils/htmlParser';
import RollbarHelper from '@app/utils/RollbarHelper';

interface ContactFormPageProps {
    pageData: PageModel;
    currentUser: UserModel;
}

const ContactFormPage: React.FC<ContactFormPageProps> = ({ pageData, currentUser, children }) => {
    const [validated, setValidated] = React.useState<'success'|'warning'|'error'|'default'>('default');
    const [message, setMessage] = React.useState('');
    const [submitMessage, setSubmitMessage] = React.useState('');
    const [submitting, setSubmitting] = React.useState(false);
    const [submitMessageVariant, setSubmitMessageVariant] = React.useState<'success'|'warning'|'default'|'danger'|'info'|undefined>('default');

    /**
     * Event handler to update the contact form message state on change
     * @param message 
     */
    const maybeUpdateMessageText = (message: string) => {
        if (message) {
            setMessage(message);
            setValidated('success');
        } else {
            setMessage('');
            setValidated('error');
        }
    };

    /**
     * Submit handler for the contact form.
     * 
     * @param e 
     */
    const submitContactForm = (e: React.SyntheticEvent) => {
        e.preventDefault();
        // If we're already submitting, short circuit the submit handler
        if (submitting) {
            return;
        }
        setSubmitMessage('');
        setSubmitting(true);
        const baseUrl: string = process.env.API_BASEURL ?? '';
        const url = baseUrl + ApiEndpoints.CONTACT_FORM_EMAIL;
        axios.post(
            url,
            {
                pageId: pageData.id,
                fpuId: currentUser.currentFpuId,
                message: message
            },
            {
                withCredentials: true,
                headers: {
                    "Accept": "application/json"
                }
            }
        )
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        .then(response => {
            setSubmitMessageVariant('success');
            setSubmitMessage('Your message has been sent!');
            setMessage('');
            setValidated('default');
        })
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        .catch(error => {
            RollbarHelper.logRequestError(error, "submitContactForm()");
            setSubmitMessageVariant('danger');
            setSubmitMessage('An error occurred while attempting to send your message. Please try again.');
        })
        .finally(() => setSubmitting(false));
    };


    return (
        <>
            <PageSection>
                <Title headingLevel="h1" className="pf-u-mb-md">{pageData.title}</Title>
                <div className="pf-c-content">
                    {htmlParser.htmlToJSX(pageData.content ?? '')}
                </div>
                {children}
            </PageSection>

            {/* Contact Form */}
            <PageSection>
                <Form className="pf-u-w-50-on-md" onSubmit={submitContactForm}>
                    {submitMessage &&
                        <Alert
                            title={submitMessageVariant === 'success' ? 'Message Sent' : 'Error Sending Message'}
                            variant={submitMessageVariant}
                            className="pf-u-mb-md"
                            role="alert"
                        >
                            {submitMessage}
                        </Alert>
                    }
                    <FormGroup
                        label="Your Message"
                        type="string"
                        helperTextInvalid="Please provide a message"
                        fieldId="contact-form-message"
                        validated={validated}
                    >
                        <TextArea 
                            id="contact-form-message"
                            value={message}
                            onChange={maybeUpdateMessageText}
                            isRequired
                            validated={validated}
                        />
                    </FormGroup>
                    <Button
                        isDisabled={validated !== 'success'}
                        isInline
                        type="submit"
                        variant="primary"
                    >
                        Submit
                    </Button>
                </Form>
            </PageSection>
        </>
    )

};

export default ContactFormPage;